"use client";
import BodyLineText from "@/components/common/text/bodyline-text";
import HeadlineText from "@/components/common/text/headline-text";
import ScreenLayout from "@/components/layout/screen-layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Image, { StaticImageData } from "next/image";
import { DynamicLinkButton } from "../../dynamic-link-button/dynamic-link-button";
import { useRouter } from "next/navigation";

type ServiceCard = {
  title: string;
  description: string;
  image?: string | StaticImageData;
  buttonResources?: {
    label: string;
    href: string;
  }[];
};

interface ServicesCardGridSectionProps {
  heading: string;
  subheading: string;
  services: ServiceCard[];
  middleImages: {
    image: StaticImageData;
    imageSrc: string;
  }[];
  buttonResources?: {
    label: string;
    href: string;
  }[];
}

export default function ServicesCardGridSection({
  heading,
  subheading,
  services,
  middleImages,
  buttonResources,
}: ServicesCardGridSectionProps) {
  const router = useRouter();
  const topServices = services.slice(0, 3);
  const bottomServices = services.slice(3, 6);

  return (
    <div className="pb-10 md:pb-20 !bg-white">
      <ScreenLayout>
        <HeadlineText wrapperClassName="flex justify-center" textClassName="">
          {heading}
        </HeadlineText>
      </ScreenLayout>

      <ScreenLayout className="flex justify-center">
        <BodyLineText wrapperClassName="" textClassName="w-full lg:w-[80%]">
          {subheading}
        </BodyLineText>
      </ScreenLayout>

      <ScreenLayout className="flex justify-center">
        <div className="flex item-center justify-center gap-x-4 pt-11 flex-wrap gap-y-3">
          <DynamicLinkButton
            value={buttonResources?.[0]?.label}
            href={buttonResources?.[0]?.href}
            variant="white"
            className="shadow-md hover:shadow-lg transition-all duration-300"
          />
          <DynamicLinkButton
            value={buttonResources?.[1]?.label}
            href={buttonResources?.[1]?.href}
            variant="dark_blue"
            className="shadow-md hover:shadow-lg transition-all duration-300"
          />
        </div>
      </ScreenLayout>

      <ScreenLayout className="pt-4 md:pt-8 lg:pt-12">
        {/*Top Section*/}
        <div className="grid justify-items-center grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 3xl:gap-x-7 gap-y-6 md:gap-y-10 xl:gap-y-15 mb-5 md:mb-8">
          {topServices.map((service, index) => (
            <Card
              key={index}
              className="bg-none shadow-none min-w-[392px] p-7 overflow-hidden mb-2 md:mb-0 !border !border-zinc-500/10"
            >
              <CardContent className="text-center px-0">
                <div className="w-auto h-[196px] rounded-4xl transition-transform duration-300 ease-in-out hover:scale-110 flex items-center justify-center overflow-hidden">
                  {service.image && (
                    <Image
                      src={service.image}
                      alt={service.title}
                      className="object-contain"
                    />
                  )}
                </div>
                <div className="flex flex-col justify-center">
                  <h2 className="text-2xl font-semibold text-custom-blue py-4">
                    {service.title}
                  </h2>
                  <p className="text-base font-normal text-[#012635] opacity-50">
                    {service.description}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/*Middle Section*/}
        {(() => {
          const weeklyImage =
            middleImages.find((img) =>
              img.imageSrc.toLowerCase().includes("weekly")
            ) || middleImages[0];
          const flutterImage =
            middleImages.find((img) =>
              img.imageSrc.toLowerCase().includes("flutter")
            ) || middleImages[1];
          const profilesImage =
            middleImages.find((img) =>
              img.imageSrc.toLowerCase().includes("profile")
            ) || middleImages[3];
          const stricklandImage =
            middleImages.find(
              (img) =>
                img.imageSrc.toLowerCase().includes("strickland") ||
                img.imageSrc.toLowerCase().includes("user")
            ) || middleImages[2];
          const statsImage =
            middleImages.find((img) =>
              img.imageSrc.toLowerCase().includes("stats")
            ) || middleImages[4];

          return (
            <div className="grid justify-items-center grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 3xl:gap-x-7 gap-y-6 md:gap-y-10 xl:gap-y-15">
              <div className="flex flex-col items-center justify-center gap-y-8">
                <Image
                  src={weeklyImage.image}
                  alt={weeklyImage.imageSrc}
                  className="object-contain"
                />
                <Image
                  src={stricklandImage.image}
                  alt={stricklandImage.imageSrc}
                  className="object-contain"
                />
              </div>
              <div className="lg:col-span-1">
                <Image
                  src={flutterImage.image}
                  alt={flutterImage.imageSrc}
                  className="object-contain w-full"
                />
              </div>
              <div className="flex flex-col items-center justify-center gap-y-8">
                <Image
                  src={profilesImage.image}
                  alt={profilesImage.imageSrc}
                  className="object-contain"
                />
                <Image
                  src={statsImage.image}
                  alt={statsImage.imageSrc}
                  className="object-contain"
                />
              </div>
            </div>
          );
        })()}

        {/*Bottom Section*/}
        <div className="grid justify-items-center grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 3xl:gap-x-7 gap-y-6 md:gap-y-10 xl:gap-y-15 mt-5 md:mt-8">
          {bottomServices.map((service, index) => (
            <Card
              key={index + 3}
              className="bg-none shadow-none min-w-[392px] p-7 overflow-hidden mb-2 md:mb-0 !border !border-zinc-500/10"
            >
              <CardContent className="text-center px-0">
                <div className="w-auto h-[196px] rounded-4xl transition-transform duration-300 ease-in-out hover:scale-110 flex items-center justify-center overflow-hidden">
                  {service.image && (
                    <Image
                      src={service.image}
                      alt={service.title}
                      className="object-contain"
                    />
                  )}
                </div>
                <div className="flex flex-col justify-center">
                  <h2 className="text-2xl font-semibold text-custom-blue py-4">
                    {service.title}
                  </h2>
                  <p className="text-base font-normal text-[#012635] opacity-50">
                    {service.description}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </ScreenLayout>
    </div>
  );
}
